import { differenceInCalendarDays } from 'date-fns';
import { isUsableImageUrl } from './itineraryItemImage';
import { getTripTimelineStatus, parseTripDate } from './tripUtils';

export const ALERT = '#8C2B22';

/**
 * Partner traveler cards often link to placeholder accounts created from email
 * only. Prefer real names, then the email local-part — never the auto-suffixed
 * system username (e.g. nkamboh_mt207eh4) when an email is available.
 */
export const getTravelerDisplayName = (person, fallback = 'Traveler') => {
  if (!person) return fallback;
  const full = [person.first_name, person.last_name].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (person.first_name) return person.first_name;
  if (person.last_name) return person.last_name;
  const emailLocal = String(person.email || '').split('@')[0].trim();
  if (emailLocal) return emailLocal;
  return person.username || fallback;
};

export const getTravelerInitials = (person) => {
  const name = getTravelerDisplayName(person, '');
  if (!name) return 'T';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name[0].toUpperCase();
};

export const getTripClientName = (trip) => getTravelerDisplayName({
  first_name: trip?.owner_first_name,
  last_name: trip?.owner_last_name,
  email: trip?.owner_email,
  username: trip?.owner_username,
}, '');

export const getTripBucket = (trip, now = new Date()) => {
  if (trip?.partner_archived) return 'archived';
  if (getTripTimelineStatus(trip, now) === 'past') return 'completed';
  return 'upcoming';
};

/** Trip has not begun yet (or has no start date) — partners may permanently delete. */
export const isPartnerTripNotStarted = (trip, now = new Date()) => {
  const start = parseTripDate(trip?.start_date);
  if (!start) return true;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  return today < start;
};

/** Trip return date has passed — partners may archive (not delete). */
export const isPartnerTripCompleted = (trip, now = new Date()) => (
  getTripTimelineStatus(trip, now) === 'past'
);

export const canDeletePartnerTrip = (trip, now = new Date()) => {
  if (!trip || trip.partner_archived) return false;
  return isPartnerTripNotStarted(trip, now);
};

export const canArchivePartnerTrip = (trip, now = new Date()) => {
  if (!trip || trip.partner_archived) return false;
  return isPartnerTripCompleted(trip, now);
};

export const getTripNights = (trip) => {
  if (!trip?.start_date || !trip?.end_date) return 0;
  return Math.max(0, differenceInCalendarDays(new Date(trip.end_date), new Date(trip.start_date)));
};

export const getDestinationLabel = (value) => {
  if (!value) return '';
  return String(value).split(',')[0].trim();
};

export const isPackageExpired = (pkg, now = new Date()) => {
  if (pkg?.status === 'archived') return true;
  if (!pkg?.available_until) return false;
  return new Date(pkg.available_until) < now;
};

export const getPackageBucket = (pkg, now = new Date()) => {
  if (isPackageExpired(pkg, now)) return 'expired';
  if (pkg?.status === 'draft' || pkg?.status === 'pending_review') return 'draft';
  if (pkg?.status === 'published') return 'live';
  return pkg?.status || 'draft';
};

/** List chips: published marketplace vs published private, plus draft/expired. */
export const getPackageListFilter = (pkg, now = new Date()) => {
  const bucket = getPackageBucket(pkg, now);
  if (bucket === 'live' && pkg?.visibility === 'private_client') return 'private';
  return bucket;
};

export const packageMatchesListFilter = (pkg, filter, now = new Date()) => {
  if (!filter || filter === 'all') return true;
  return getPackageListFilter(pkg, now) === filter;
};

export const getBookingBucket = (status) => {
  if (status === 'pending') return 'awaiting';
  if (status === 'confirmed' || status === 'completed') return 'complete';
  if (['partner_confirmed', 'payment_pending', 'paid'].includes(status)) return 'confirmed';
  return status || 'awaiting';
};

export const inquiryNeedsReply = (inquiry) => {
  if (!inquiry) return false;
  if (inquiry.status === 'converted' || inquiry.status === 'closed') return false;
  if (Number(inquiry.unread_count || 0) > 0) return true;
  return ['new', 'contacted', 'booking_requested'].includes(inquiry.status);
};

export const getInquiryBucket = (inquiry) => {
  if (inquiry?.partner_archived) return 'archived';
  return inquiryNeedsReply(inquiry) ? 'needsReply' : 'answered';
};

export const getInquiryKind = (inquiry) => {
  if (inquiry?.status === 'booking_requested') return 'bookingRequest';
  if (inquiry?.inquiry_type === 'general_trip_request') return 'customRequest';
  if (inquiry?.inquiry_type === 'package_customization') return 'customization';
  return 'question';
};

/**
 * What the traveler is asking about. Package requests can gain a proposal
 * trip later, so a package link wins over a trip link.
 * @returns {'package'|'groupTrip'|'private'|'trip'}
 */
export const getInquirySubject = (inquiry) => {
  if (inquiry?.package_id) return 'package';
  if (
    inquiry?.original_source === 'dvn_private'
    || String(inquiry?.special_requests || '').includes('DVN_PRIVATE')
  ) {
    return 'private';
  }
  if (inquiry?.linked_trip_is_master) return 'groupTrip';
  return 'trip';
};

export const INQUIRY_SUBJECT_LABEL_KEYS = {
  package: 'partnerLists.subjectPackage',
  groupTrip: 'partnerLists.subjectGroupTrip',
  private: 'partnerLists.subjectPrivate',
  trip: 'partnerLists.subjectTrip',
};

export const getInquiryPreferredDates = (inquiry) => {
  if (String(inquiry?.preferred_dates || '').trim()) {
    return String(inquiry.preferred_dates).trim();
  }
  const start = inquiry?.preferred_travel_date;
  const end = inquiry?.preferred_end_date;
  if (start && end) return `${start} – ${end}`;
  return start || end || null;
};

export const getInquiryRequestDetailRows = (inquiry, labels) => {
  const rows = [];
  const destination = String(inquiry?.destination || '').trim();
  const displayTitle = getInquiryDisplayTitle(inquiry, labels.fallback);
  if (destination && destination !== displayTitle) {
    rows.push([labels.destination, destination]);
  }
  const dates = getInquiryPreferredDates(inquiry);
  if (dates) rows.push([labels.dates, dates]);
  const travelers = inquiry?.num_travelers || inquiry?.travelers;
  if (travelers) rows.push([labels.travelers, String(travelers)]);
  const budget = String(inquiry?.budget_range || '').trim();
  if (budget) rows.push([labels.budget, budget]);
  const phone = String(inquiry?.user_phone || inquiry?.phone || '').trim();
  if (phone) rows.push([labels.phone, phone]);
  return rows;
};

export const hasInquiryRequestDetails = (inquiry) => {
  if (!inquiry) return false;
  return getInquiryRequestDetailRows(inquiry, {
    fallback: '',
    destination: '',
    dates: '',
    travelers: '',
    budget: '',
    phone: '',
  }).length > 0;
};

const normalizeInquiryCompareText = (value) => String(value || '')
  .toLowerCase()
  .replace(/[–—]/g, '-')
  .replace(/\s+/g, ' ')
  .trim();

/** Auto-generated summary lines that repeat meta already shown on the partner desk. */
export const getInquirySummaryVariants = (inquiry, fallback = '') => {
  const title = String(getInquiryDisplayTitle(inquiry, fallback) || '').trim();
  const packageTitle = String(inquiry?.package_title || '').trim();
  const dates = getInquiryPreferredDates(inquiry);
  const destination = String(inquiry?.destination || '').trim();
  const travelers = inquiry?.num_travelers || inquiry?.travelers;
  const bookingRef = String(inquiry?.booking_reference || '').trim();
  const variants = new Set();

  if (title) variants.add(title);
  if (packageTitle) variants.add(packageTitle);
  if (destination) variants.add(destination);
  if (title && dates) {
    variants.add(`${title} · ${dates}`);
    if (destination) variants.add(`${title} · ${dates} · ${destination}`);
  }
  if (destination && dates) {
    variants.add(`${destination} · ${dates}`);
    variants.add(`${destination} · ${dates} · ${destination}`);
  }
  if (bookingRef && travelers && dates) {
    variants.add(`${bookingRef} · ${travelers} traveler · ${dates}`);
    variants.add(`${bookingRef} · ${travelers} travelers · ${dates}`);
  }
  return [...variants].filter(Boolean);
};

export const isRedundantInquiryThreadMessage = (messageText, inquiry, fallback = '') => {
  const text = normalizeInquiryCompareText(messageText);
  if (!text) return true;
  return getInquirySummaryVariants(inquiry, fallback)
    .some((variant) => normalizeInquiryCompareText(variant) === text);
};

/** Drop traveler messages that only restate package/dates already in the meta grid. */
export const filterVisibleInquiryMessages = (messages, inquiry, fallback = '') => {
  if (!Array.isArray(messages)) return [];
  return messages.filter((msg) => {
    if (msg?.sender_type === 'partner') return true;
    return !isRedundantInquiryThreadMessage(msg?.message, inquiry, fallback);
  });
};

/** Primary label for inquiry rows — destination, package name, or message snippet. */
export const getInquiryDisplayTitle = (inquiry, fallback = 'Custom trip request') => {
  const destination = String(inquiry?.destination || '').trim();
  if (inquiry?.inquiry_type === 'general_trip_request' && destination) {
    return destination;
  }

  const packageTitle = String(inquiry?.package_title || '').trim();
  if (packageTitle) return packageTitle;

  if (destination) return destination;

  const message = String(inquiry?.message || inquiry?.last_message || '').trim();
  if (message) {
    const firstLine = message.split('\n')[0].trim();
    if (firstLine.length > 72) return `${firstLine.slice(0, 72).trim()}…`;
    return firstLine;
  }

  const partnerName = String(inquiry?.partner_name || inquiry?.partnerName || '').trim();
  if (partnerName) return `${partnerName} trip request`;

  return fallback;
};

export const getInquiryDisplaySubtitle = (inquiry, displayTitle) => {
  const title = displayTitle || getInquiryDisplayTitle(inquiry);
  const parts = [];
  const partnerName = String(inquiry?.partner_name || inquiry?.partnerName || '').trim();
  const destination = String(inquiry?.destination || '').trim();

  if (partnerName && !title.includes(partnerName)) {
    parts.push(partnerName);
  }
  if (destination && destination !== title) {
    parts.push(destination);
  }
  return parts.join(' · ');
};

export const matchesQuery = (haystack, query) => {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return true;
  return haystack.some((value) => String(value || '').toLowerCase().includes(needle));
};

export const formatMoney = (value) => {
  const amount = Number(value || 0);
  return `$${amount.toLocaleString()}`;
};

export const averageReplyHours = (inquiries = []) => {
  const samples = inquiries
    .map((inquiry) => {
      if (!inquiry.created_at || !inquiry.partner_response_at) return null;
      const created = new Date(inquiry.created_at);
      const replied = new Date(inquiry.partner_response_at);
      const hours = (replied - created) / 36e5;
      return hours >= 0 ? hours : null;
    })
    .filter((hours) => hours != null);
  if (!samples.length) return null;
  return Math.round(samples.reduce((sum, hours) => sum + hours, 0) / samples.length);
};

const FALLBACK_COVERS = [
  'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1600&h=800&fit=crop&q=80',
  'https://images.unsplash.com/photo-1533105079780-92b9be482077?w=1600&h=800&fit=crop&q=80',
  'https://images.unsplash.com/photo-1516483638261-f4dbaf036963?w=1600&h=800&fit=crop&q=80',
];

export const getDestinationPhotoUrl = (destination, seed) => {
  const place = getDestinationLabel(destination) || String(seed || '');
  let hash = 0;
  for (let i = 0; i < place.length; i += 1) {
    hash = ((hash << 5) - hash) + place.charCodeAt(i);
    hash |= 0;
  }
  return FALLBACK_COVERS[Math.abs(hash) % FALLBACK_COVERS.length];
};

export const getGalleryUrls = (gallery) => {
  try {
    const items = typeof gallery === 'string' ? JSON.parse(gallery) : gallery;
    if (!Array.isArray(items)) return [];
    return items
      .map((item) => item?.url || item?.src || (typeof item === 'string' ? item : ''))
      .filter(Boolean);
  } catch (err) {
    return [];
  }
};

export const firstGalleryImage = (gallery) => {
  try {
    const items = typeof gallery === 'string' ? JSON.parse(gallery) : gallery;
    if (!Array.isArray(items) || !items.length) return '';
    const first = items[0];
    return first?.url || first?.src || (typeof first === 'string' ? first : '');
  } catch (err) {
    return '';
  }
};

export const getPackageCoverSrc = (pkg) => {
  if (isUsableImageUrl(pkg?.cover_image)) return String(pkg.cover_image).trim();
  const gallery = firstGalleryImage(pkg?.gallery);
  if (isUsableImageUrl(gallery)) return gallery;
  return getDestinationPhotoUrl(pkg?.destination, pkg?.id);
};

export const bookingsToCsv = (bookings = []) => {
  const header = ['Reference', 'Customer', 'Email', 'Package', 'Travellers', 'Travel dates', 'Gross', 'Payout', 'Status'];
  const rows = bookings.map((booking) => [
    booking.booking_reference,
    booking.user_name,
    booking.user_email,
    booking.package_title,
    booking.num_travelers || 1,
    [booking.travel_date, booking.return_date].filter(Boolean).join(' - '),
    booking.total_price,
    booking.partner_payout_amount,
    booking.status,
  ]);
  return [header, ...rows]
    .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
};
